/* eslint-disable react/display-name */
/*
 * @Author: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @Date: 2025-04-23 12:01:29
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2026-03-04 21:16:36
 */

/**
 * ViewerV2 组件
 *
 * 功能：照片查看器主组件，整合所有子组件并管理共享状态
 * 核心功能：
 * - 照片/视频浏览与切换
 * - 缩放、旋转、全屏等操作
 * - 水印信息展示和去除
 * - 文件夹管理
 * - 评论功能
 * - AI照片增强（变清晰）
 * - 照片对比模式
 *
 * 组件结构：
 * - ViewerHeader：顶部导航栏
 * - PhotoContent：照片/视频主内容区
 * - ActionBar：操作按钮工具栏
 * - PhotoGallery：底部缩略图浏览
 * - InfoPanel：右侧信息面板
 * - AiProcessingOverlay：AI处理状态覆盖层
 */
import React, { useEffect, forwardRef, useImperativeHandle } from 'react'
import fetch from 'isomorphic-fetch'
import { Modal, message, notification } from 'antd'
import FileSaver from 'file-saver'
import classnames from 'classnames'
import CONFIG from '../../../constants/config'
import {
  fetchWatermarkByUrl,
  deletePhoto as allPhotosDeletePhoto,
  createProducePhotoTask,
  createComments,
  aiClearCreateTask,
  aiClearQueryTask,
  aiClearCancelTask
} from '../../../actions/photo'
import sensors from '../../../utils/sensors'
import { capturePromiseException } from '../../../utils/sentryUtils'
import { fetchPhotoInfo, modifyPhotoFolders } from '../../../actions/folder'
import actionTypes from '../../../constants/actionTypes'
import RemoveWatermarkModal from '../RemoveWatermarkModal'
import ViewerHeader from './components/ViewerHeader'
import PhotoGallery from './components/PhotoGallery'
import PhotoContent from './components/PhotoContent'
import ActionBar from './components/ActionBar'
import InfoPanel from './components/InfoPanel'
import AiProcessingOverlay from './components/AiProcessingOverlay'
import { useViewerState } from './hooks/useViewerState'
import { useImageHandlers } from './hooks/useImageHandlers'
import { usePhotoData } from './hooks/usePhotoData'
import { usePhotoNavigation } from './hooks/usePhotoNavigation'
import { useAiClarity } from './hooks/useAiClarity'
import { Member, ViewerProps, ViewerRef } from './types'
import { openRectifyDrawerWithPhotos } from '../../../slices/rectifySlice'
import { setPhotoPickerPhotos } from '../../../actions/photoPicker'
import { useNavigate } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '../../../app/hooks'

const Viewer = forwardRef<ViewerRef, ViewerProps>((props, ref) => {
  // 使用 hooks 替代 connect
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const groupId = useAppSelector((state) => state.group.groupId)
  const reduxMembers = useAppSelector((state) => state.group.members)
  const userId = useAppSelector((state) => state.main.user?.user_id)
  const myRoleFromRedux = useAppSelector((state) => state.group.myRole)
  const viewPermFromRedux = useAppSelector((state) => state.group.viewPerm)
  const isVip = useAppSelector((state) => state.group.isVip)
  const reduxOriginalPhotos = useAppSelector(
    (state) => state.photo.originalPhotos
  )
  const isPhotoClarity = useAppSelector(
    (state) => state.main.abTestConfig?.PhotoEnhancementAB
  )
  const aiVisualSearchAB = useAppSelector(
    (state) => state.photo.aiVisualSearchAB
  )

  // 创建 dispatch 包装的 action creators
  const dispatchFetchWatermarkByUrl = (data: any) =>
    dispatch(fetchWatermarkByUrl(data))
  const dispatchAllPhotosDeletePhoto = (data: any) =>
    dispatch(allPhotosDeletePhoto(data))
  const dispatchFetchPhotoInfo = (data: any) => dispatch(fetchPhotoInfo(data))
  const dispatchModifyPhotoFolders = (data: any) =>
    dispatch(modifyPhotoFolders(data))
  const dispatchCreateProducePhotoTask = (data: any) =>
    dispatch(createProducePhotoTask(data))
  const dispatchCreateComments = (data: any) => dispatch(createComments(data))
  const dispatchAiClearCreateTask = (data: any) =>
    dispatch(aiClearCreateTask(data))
  const dispatchAiClearQueryTask = (data: any) =>
    dispatch(aiClearQueryTask(data))
  const dispatchAiClearCancelTask = (data: any) =>
    dispatch(aiClearCancelTask(data))

  // 合并 props 中的值和 redux 中的值
  const members = props.members || reduxMembers

  const {
    state,
    setState,
    imgRef,
    mouseDown,
    downPosition,
    movement,
    offsetX,
    offsetY,
    currentVisible,
    wmTimer,
    lastTaskCreationTime,
    currentTaskId,
    originalPhotos,
    scaleChanged,
    setScaleChanged,
    hdButtonChanged,
    setHdButtonChanged
  } = useViewerState()

  // 构建 propsForHooks 用于传递给子 hooks
  const propsForHooks = {
    ...props,
    groupId,
    members,
    userId,
    myRole: props.myRole ?? myRoleFromRedux,
    viewPerm: props.viewPerm ?? viewPermFromRedux,
    isVip,
    originalPhotos: reduxOriginalPhotos,
    isPhotoClarity,
    aiVisualSearchAB,
    dispatch,
    fetchWatermarkByUrl: dispatchFetchWatermarkByUrl,
    allPhotosDeletePhoto: dispatchAllPhotosDeletePhoto,
    fetchPhotoInfo: dispatchFetchPhotoInfo,
    modifyPhotoFolders: dispatchModifyPhotoFolders,
    createProducePhotoTask: dispatchCreateProducePhotoTask,
    createComments: dispatchCreateComments,
    aiClearCreateTask: dispatchAiClearCreateTask,
    aiClearQueryTask: dispatchAiClearQueryTask,
    aiClearCancelTask: dispatchAiClearCancelTask
  }

  const {
    handleReportSA,
    handlePhotoClearSA,
    handleInfo,
    handleRotate,
    handleMouseDown,
    handleMouseUpOrOut,
    handleMouseMove,
    handleZoomOut,
    handleZoomIn,
    handleImageMouseWheel,
    handleMouseWheel,
    onScroll
  } = useImageHandlers({
    state,
    setState,
    imgRef,
    mouseDown,
    downPosition,
    movement,
    offsetX,
    offsetY,
    props: propsForHooks,
    setScaleChanged
  })

  const { handleFetchWatermark, handleFetchFolderInfo } = usePhotoData({
    state,
    setState,
    currentVisible,
    props: propsForHooks
  })

  const { handlePrev, handleNext } = usePhotoNavigation({
    state,
    setState,
    wmTimer,
    handleReportSA,
    handleFetchWatermark,
    handleFetchFolderInfo,
    props: propsForHooks
  })

  const {
    canDelete,
    canRectify,
    isHideWatermarkInfo,
    isHideFoldersInfo,
    showWatermarkAction = true
  } = props

  const myRole = props.myRole ?? myRoleFromRedux
  const viewPerm = props.viewPerm ?? viewPermFromRedux

  const {
    handleCancelAiClear,
    handleFailureConfirm,
    handleRetry,
    handleHdDisplay
  } = useAiClarity({
    state,
    setState,
    props: propsForHooks,
    imgRef,
    originalPhotos,
    lastTaskCreationTime,
    currentTaskId,
    handlePhotoClearSA,
    handleReportSA
  })

  useEffect(() => {
    document.addEventListener('keydown', eventListeners)
    return () => {
      document.removeEventListener('keydown', eventListeners)
      if (wmTimer.current) {
        clearTimeout(wmTimer.current)
      }
    }
  }, [userId])

  // 自动显示信息面板
  useEffect(() => {
    if (props.autoShowInfoPanel && state.visible) {
      setState((prevState) => ({
        ...prevState,
        isHideInfo: false
      }))
    }
  }, [props.autoShowInfoPanel, state.visible])

  // 打开时上报埋点
  useEffect(() => {
    if (state.visible) {
      handleReportSA('enterPage')
    }
  }, [state.visible])

  // useEffect 监听 scale 变化
  useEffect(() => {
    if (scaleChanged && state.scale > 100 && !state.isComparisonMode) {
      setScaleChanged(false)
      setState((prevState) => ({
        ...prevState,
        showHdButton: true
      }))
      setHdButtonChanged(true)
    }
  }, [scaleChanged, state.scale, state.isComparisonMode])

  // useEffect 监听 showHdButton 变化
  useEffect(() => {
    if (hdButtonChanged) {
      setHdButtonChanged(false)

      const { showHdButton, aiProcessing, isComparisonMode } = state
      const { photos, isPhotoClarity } = props
      const photo = photos[state.activeIndex]
      const curPhoto = photos[state.activeIndex]
      const canRemoveWatermark =
        state.watermarks.length === 1 &&
        curPhoto?.sourceType === 0 &&
        curPhoto?.hasWatermark &&
        curPhoto?.mediaType === 0

      // 只有当所有条件都满足时，才发送埋点
      if (
        photo?.mediaType === 0 &&
        showHdButton &&
        canRemoveWatermark &&
        !aiProcessing &&
        !isComparisonMode &&
        isPhotoClarity
      ) {
        handleReportSA('photoBeClearShow')
      }
    }
  }, [
    hdButtonChanged,
    state,
    props.photos,
    props.isPhotoClarity,
    handleReportSA
  ])

  const eventListeners = (e: KeyboardEvent) => {
    if (state.visible) {
      if (e.keyCode === 37 && state.activeIndex > 0) {
        handlePrev()
        const galleryElement = document.getElementsByClassName(
          'gallery'
        )[0] as HTMLElement
        if (galleryElement) {
          galleryElement.scrollBy(-109, 0)
        }
      } else if (
        e.keyCode === 39 &&
        state.activeIndex < props.photos.length - 1
      ) {
        handleNext()
        const galleryElement = document.getElementsByClassName(
          'gallery'
        )[0] as HTMLElement
        if (galleryElement) {
          galleryElement.scrollBy(109, 0)
        }
      } else if (e.keyCode === 27) {
        handleClose()
      }
    }
  }

  const handleAddEventListener = () => {
    document.addEventListener('keydown', eventListeners)
  }

  const handleRemoveEventListener = () => {
    document.removeEventListener('keydown', eventListeners)
  }

  const handleSave = () => {
    handleReportSA('download')
    handlePhotoClearSA('download')
    const { photos } = props
    const { activeIndex } = state
    const photo = photos[activeIndex]
    const url = photo?.mediaType === 0 ? photo?.largeURL : photo?.videoURL
    setState((prevState) => ({ ...prevState, saveLoading: true }))
    fetch(url)
      .then((res) => res.blob())
      .then((blob) => {
        FileSaver.saveAs(blob, photo?.saveName)
        setState((prevState) => ({ ...prevState, saveLoading: false }))
      })
      .catch((err: Error) => {
        setState((prevState) => ({ ...prevState, saveLoading: false }))
        capturePromiseException(err)
      })
  }
  const handleDelete = () => {
    handleReportSA('delete')
    handlePhotoClearSA('delete')
    const { photos } = props
    const { activeIndex, isComparisonMode } = state

    const deleteAction = (index: number) => {
      return dispatchAllPhotosDeletePhoto({
        group_id: groupId,
        photo_list: [photos[index]?.seq]
      })
        .then(() => {
          // 如果是在对比模式下删除原图，则返回全部照片页面并刷新
          if (isComparisonMode && index === 0) {
            setState((prevState) => ({
              ...prevState,
              visible: false,
              isComparisonMode: false
            }))
            window.location.href = '/photo'
          }
        })
        .catch(capturePromiseException)
    }

    // 在对比模式下删除原图时使用不同的确认文案
    const modalTitle =
      isComparisonMode && activeIndex === 0
        ? '是否要删除原图，删除后无法找回'
        : '是否删除照片/视频？'

    Modal.confirm({
      title: modalTitle,
      cancelText: '取消',
      okText: '删除',
      okType: 'danger',
      style: {
        top: '40%'
      },
      onOk: () => {
        // 在非对比模式下的正常逻辑
        if (!isComparisonMode) {
          if (activeIndex < photos.length - 1) {
            handleNext()
          } else if (activeIndex > 0) {
            handlePrev()
          } else {
            setState((prevState) => ({ ...prevState, visible: false }))
          }
        }
        // 执行删除操作
        deleteAction(activeIndex)
        handlePhotoClearSA('confirmDelete')
      }
    })
  }

  const createRemoveWatermarkTask = () => {
    handleReportSA('removeWatermark')
    handlePhotoClearSA('removeWatermark')

    const { photos } = props
    const { activeIndex } = state
    const srcUrls = [photos[activeIndex]?.largeURL]
    const fileName = photos[activeIndex]?.file

    dispatchCreateProducePhotoTask({
      userID: userId,
      groupID: groupId,
      srcUrls,
      type: 1,
      source: 'web_remove_watermark'
    }).then((res: any) => {
      if (!isVip) {
        sensors.track('web_click_button_workgroup_go_vip_trigger', {
          place: 'photoPreview',
          functionType: 'singleRemoveWatermark'
        })
      }

      setState((prevState) => ({
        ...prevState,
        removeWatermarkModalStatus: {
          visible: true,
          taskID: res.taskID,
          fileName: fileName || ''
        }
      }))
    })
  }

  // 隐患识别
  const handleAiSecurityRecognition = () => {
    // 获取当前照片信息
    const currentPhoto = props.photos[state.activeIndex]
    dispatch(setPhotoPickerPhotos([currentPhoto], 'ai-security'))
    navigate(encodeURI('/ai/security'))
  }

  const createRectifyTask = () => {
    handleReportSA('提整改')

    // 获取当前照片信息
    const currentPhoto = props.photos[state.activeIndex]
    if (!currentPhoto) {
      message.error('当前没有照片可以发起整改')
      return
    }

    // 检查媒体类型，区分图片和视频
    const mediaType = currentPhoto.mediaType
    const isVideo = mediaType === 1

    // 获取用于展示的URL（图片用smallURL，视频用smallURL作为缩略图）
    const smallURL = currentPhoto.smallURL

    // 获取用于上传/提交的URL（图片用largeURL，视频用videoURL）
    const uploadUrl = isVideo
      ? currentPhoto.videoURL
      : currentPhoto.largeURL || currentPhoto.videoURL

    // 确保有可用的URL
    if (!smallURL || !uploadUrl) {
      console.error('无法获取媒体URL:', currentPhoto)
      message.error(`${isVideo ? '视频' : '照片'}URL获取失败，无法发起整改`)
      return
    }

    // 构建完整的媒体对象
    const photoForRectify = {
      smallURL: smallURL, // 小图URL
      largeURL: currentPhoto.largeURL, // 大图URL
      videoURL: currentPhoto.videoURL, // 视频URL
      file: currentPhoto.file, // 文件名
      mediaType: mediaType, // 媒体类型
      imageSize: currentPhoto.imageSize, // 图片尺寸
      videoInfo: currentPhoto.videoInfo, // 视频尺寸
      userID: currentPhoto.userID,
      extension: currentPhoto.extension
    }

    // 打开整改抽屉并传递当前照片，设置为锁定模式
    dispatch(
      openRectifyDrawerWithPhotos({
        photos: [photoForRectify],
        isLocked: true // 从照片查看器进入，锁定照片，不允许删除/新增
      })
    )
  }

  const handleViewByPerson = (member: Member) => {
    handleReportSA('avatar')
    window.open(`/photo?&member_id=${encodeURIComponent(member.user_id)}`)
  }

  const handleClose = () => {
    handleReportSA('close')
    handlePhotoClearSA('back')

    // 如果是对比模式，恢复原始照片列表并保持查看器打开
    if (state.isComparisonMode && originalPhotos.current) {
      // 保存当前索引，以便恢复到正确的照片
      const originalIndex = state.originalIndexBeforeComparison || 0

      // 更新Redux中的照片列表回到原始状态
      dispatch({
        type: actionTypes.SET_PHOTOS,
        data: originalPhotos.current
      })

      // 重置状态，但保持查看器打开
      setState((prevState) => ({
        ...prevState,
        scale: 100,
        rotate: 0,
        activeIndex: originalIndex,
        showHdButton: false,
        isComparisonMode: false, // 重置对比模式标记
        watermarkLoading: true // 可能需要重新加载水印信息
      }))

      // 重新获取水印和文件夹信息
      handleFetchWatermark(originalIndex)
      handleFetchFolderInfo(originalIndex)

      // 清除原始照片的引用
      originalPhotos.current = null
      return // 不继续执行关闭查看器的逻辑
    }

    // 如果不是对比模式，执行正常的关闭行为
    setState((prevState) => ({
      ...prevState,
      scale: 100,
      rotate: 0,
      isFullScreen: false,
      isHideInfo: true,
      visible: false,
      showHdButton: false,
      aiProcessing: false,
      isComparisonMode: false // 重置对比模式标记
    }))

    dispatch({
      type: actionTypes.SET_CURRENT_VIEW_PHOTO,
      data: null
    })
  }

  const handleAddToFolder = ({
    folderIDs,
    submitCallback
  }: {
    folderIDs: number[]
    submitCallback: () => void
  }) => {
    const { photos } = props
    const { activeIndex, folders } = state
    const photo = photos[activeIndex]
    const id = photo?.id
    const seq = photo?.seq
    const photoID = id || `${seq}`
    const folderIDList = folders.map((item) => item.folderID)

    dispatchModifyPhotoFolders({
      groupID: groupId,
      folderIDs: [...folderIDList, ...folderIDs],
      photoID,
      source: 4
    }).then(() => {
      submitCallback()
      handleFetchFolderInfo(activeIndex)
    })
  }

  const handleSaveComment = (value: string) => {
    const { activeIndex } = state
    const { photos } = props

    dispatchCreateComments({
      groupID: groupId,
      photoID: photos[activeIndex]?.seq?.toString(),
      contents: value
    })
      .then((res: any) => {
        if (res.status === 0) {
          // 添加评论后，将本地 store 中的图片评论更新，以显示最新评论，避免刷新整个页面
          const temp = photos.map((p, i) => {
            if (i !== activeIndex) return p
            return {
              ...p,
              comments: [
                ...(p.comments || []),
                {
                  contents: [{ type: 'text', content: value }],
                  createdAt: new Date().getTime(),
                  userInfo: {
                    nickname: members.find(
                      (item: any) => item.user_id === userId
                    )?.nickname
                  }
                }
              ]
            }
          })

          dispatch({
            type: actionTypes.SET_PHOTOS,
            data: temp
          })

          notification.success({
            className: 'notificationStyle',
            placement: 'bottomRight',
            message: '评论成功',
            bottom: 120,
            description: '评论内容可在照片台账表中导出',
            duration: 3,
            style: { zIndex: 1100 }
          })
        }
      })
      .catch(capturePromiseException)
  }

  // 添加useImperativeHandle来暴露方法给ref
  useImperativeHandle(ref, () => ({
    view: (index = 0) => {
      setState((prevState) => ({
        ...prevState,
        activeIndex: index,
        visible: true,
        scale: 100,
        rotate: 0
      }))
      handleFetchWatermark(index)
      handleFetchFolderInfo(index)

      // 添加自动滚动逻辑
      setTimeout(() => {
        const photoFrames = document.getElementsByClassName('photo-frame')
        if (photoFrames && photoFrames[index]) {
          photoFrames[index].scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
          })
        }
      }, 100)
    }
  }))

  if (
    state.visible &&
    state.activeIndex > -1 &&
    props.photos[state.activeIndex]
  ) {
    const member = state.authorUserId
      ? members.find((member) => member.user_id === state.authorUserId)
      : null
    const photo = props.photos[state.activeIndex]

    return (
      <>
        <div className="viewer">
          <div
            className={classnames('viewer-panel', {
              'full-screen': isHideWatermarkInfo
            })}
            style={
              state.isFullScreen || state.isHideInfo ? { width: '100%' } : {}
            }
          >
            {/* 展示顶部导航栏，包含作者信息和关闭按钮 */}
            <ViewerHeader
              member={member}
              photo={photo as any}
              isComparisonMode={state.isComparisonMode}
              aiProcessing={state.aiProcessing}
              onClose={handleClose}
              onViewByPerson={handleViewByPerson}
              handleReportSA={handleReportSA}
              groupId={groupId}
              myRole={myRole}
            />
            <div
              className="viewer-content"
              onWheel={(e) => {
                e.stopPropagation()
                handleImageMouseWheel(e.deltaY)
              }}
            >
              {/* 展示照片/视频主内容区域，包含导航按钮和鼠标交互 */}
              <PhotoContent
                photo={photo}
                // 当前activeIndex的前一张照片的isLimitViewByPhotoCount是否为true
                isLimitViewByPhotoCountPrev={
                  state.activeIndex > 0
                    ? props.photos[state.activeIndex - 1]
                        ?.isLimitViewByPhotoCount
                    : false
                }
                // 当前activeIndex的下一张照片的isLimitViewByPhotoCount是否为true
                isLimitViewByPhotoCountNext={
                  state.activeIndex < props.photos.length - 1
                    ? props.photos[state.activeIndex + 1]
                        ?.isLimitViewByPhotoCount
                    : false
                }
                activeIndex={state.activeIndex}
                totalPhotos={props.photos.length}
                scale={state.scale}
                rotate={state.rotate}
                imgRef={imgRef}
                aiProcessing={state.aiProcessing}
                onPrev={handlePrev}
                onNext={handleNext}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUpOrOut}
                onMouseOut={handleMouseUpOrOut}
                onMouseMove={handleMouseMove}
                onVideoPlay={() => handleReportSA('videoPlay')}
              >
                {/* 显示操作按钮栏，包含全屏、旋转、缩放等功能 */}
                <ActionBar
                  photo={photo}
                  scale={state.scale}
                  isFullScreen={state.isFullScreen}
                  aiProcessing={state.aiProcessing}
                  isComparisonMode={state.isComparisonMode}
                  activeIndex={state.activeIndex}
                  canDelete={canDelete === false ? false : true}
                  canRectify={canRectify === false ? false : true}
                  canAiSecurity={props.canAiSecurity !== false}
                  canRemoveWatermark={
                    state.activeIndex > -1 &&
                    showWatermarkAction &&
                    state.watermarks.length === 1 &&
                    props.photos[state.activeIndex]?.sourceType !== 1 &&
                    props.photos[state.activeIndex]?.sourceType !== 4 &&
                    !!props.photos[state.activeIndex]?.hasWatermark &&
                    props.photos[state.activeIndex]?.mediaType === 0
                  }
                  showHdButton={state.showHdButton}
                  isPhotoClarity={isPhotoClarity}
                  aiVisualSearchAB={aiVisualSearchAB}
                  saveLoading={state.saveLoading}
                  isHideInfo={state.isHideInfo}
                  isShowComments={props.isShowComments || false}
                  comments={photo?.comments || []}
                  onFullScreen={() => {
                    handleReportSA('fullScreen')
                    handlePhotoClearSA('fullScreen')
                    setState((prevState) => ({
                      ...prevState,
                      isFullScreen: true
                    }))
                  }}
                  onExitFullScreen={() =>
                    setState((prevState) => ({
                      ...prevState,
                      isFullScreen: false
                    }))
                  }
                  onRotate={() => {
                    handleReportSA('rotate')
                    handlePhotoClearSA('rotate')
                    handleRotate(90)
                  }}
                  onZoomOut={handleZoomOut}
                  onZoomIn={handleZoomIn}
                  onInfo={handleInfo}
                  onRemoveWatermark={createRemoveWatermarkTask}
                  onCreateRectifyTask={createRectifyTask}
                  onReportSA={handleReportSA}
                  onDelete={handleDelete}
                  onSave={handleSave}
                  onHdDisplay={handleHdDisplay}
                  onSaveComment={handleSaveComment}
                  onRemoveEventListener={handleRemoveEventListener}
                  onAddEventListener={handleAddEventListener}
                  onAiSecurityRecognition={handleAiSecurityRecognition}
                />
              </PhotoContent>
            </div>

            {/* 底部缩略图列表，用于快速切换照片 */}
            <PhotoGallery
              photos={props.photos}
              activeIndex={state.activeIndex}
              isFullScreen={state.isFullScreen}
              aiProcessing={state.aiProcessing}
              isComparisonMode={state.isComparisonMode}
              onScroll={onScroll}
              onWheel={handleMouseWheel}
              onSelectPhoto={(index) => {
                handleReportSA('smallPic')
                handleFetchWatermark(index)
                handleFetchFolderInfo(index)
                setState((prevState) => ({
                  ...prevState,
                  activeIndex: index,
                  scale: 100,
                  rotate: 0,
                  watermarkLoading: true,
                  showHdButton: false
                }))
              }}
            />
          </div>

          {/* 右侧信息面板，展示水印信息、文件夹和评论 */}
          {!isHideWatermarkInfo && (
            <InfoPanel
              isFullScreen={state.isFullScreen}
              isHideInfo={state.isHideInfo}
              watermarkLoading={state.watermarkLoading}
              watermarks={state.watermarks}
              member={member}
              isShowFolders={
                !isHideFoldersInfo &&
                (myRole === CONFIG.memberRoleTypes.ADMIN ||
                  myRole === CONFIG.memberRoleTypes.MASTER_ADMIN ||
                  viewPerm === 0)
              }
              folders={state.folders}
              isShowAddToFolderBtn={state.isShowAddToFolderBtn}
              isShowComments={props.isShowComments || false}
              photo={photo as any}
              canRemoveWatermark={
                state.activeIndex > -1 &&
                showWatermarkAction &&
                state.watermarks.length === 1 &&
                props.photos[state.activeIndex]?.sourceType !== 1 &&
                props.photos[state.activeIndex]?.sourceType !== 4 &&
                !!props.photos[state.activeIndex]?.hasWatermark &&
                props.photos[state.activeIndex]?.mediaType === 0
              }
              onViewByPerson={handleViewByPerson as any}
              onRemoveWatermark={createRemoveWatermarkTask}
              onAddToFolder={handleAddToFolder}
              onSaveComment={handleSaveComment}
              onReportSA={handleReportSA}
              onRemoveEventListener={handleRemoveEventListener}
              onAddEventListener={handleAddEventListener}
            />
          )}
        </div>
        {/* 水印去除弹窗，处理水印移除任务 */}
        <RemoveWatermarkModal
          {...state.removeWatermarkModalStatus}
          photoNum={1}
          onRetry={createRemoveWatermarkTask}
          onCancel={() => {
            setState((prevState) => ({
              ...prevState,
              removeWatermarkModalStatus: {
                visible: false,
                taskID: null,
                fileName: ''
              }
            }))
          }}
        />
        {/* AI处理覆盖层，展示AI处理状态和失败提示 */}
        <AiProcessingOverlay
          aiProcessing={state.aiProcessing}
          aiProcessingFailed={state.aiProcessingFailed}
          onCancelAiClear={handleCancelAiClear}
          onFailureConfirm={handleFailureConfirm}
          onRetry={handleRetry}
        />
      </>
    )
  } else {
    return null
  }
})

// 导出组件和类型
export type { ViewerRef } from './types'
export default Viewer
